import { WeatherMode } from "features/map/control/types"
import {
    WEATHER_COVERAGE_DAYS_FORWARD,
    WEATHER_RADAR_HOURS_FORWARD,
    WeatherLayerType,
} from "@nova/constants"
import {
    forecastRunMs,
    publishedForecastHorizonMs,
    publishedHistoryStartMs,
} from "./weatherTileSource"

const HOUR_MS = 60 * 60 * 1000

export const COVERAGE_CAP_MS = 3 * 24 * HOUR_MS

const MODE_TILE_TYPE: Record<WeatherMode, WeatherLayerType> = {
    wind: "wind10m",
    precipitation: "precipitation",
    lightning: "lightning",
    radar: "radar",
}

const MODE_FORWARD_REACH_MS: Record<WeatherMode, number> = {
    wind: WEATHER_COVERAGE_DAYS_FORWARD * 24 * HOUR_MS,
    precipitation: WEATHER_COVERAGE_DAYS_FORWARD * 24 * HOUR_MS,
    lightning: WEATHER_COVERAGE_DAYS_FORWARD * 24 * HOUR_MS,
    radar: WEATHER_RADAR_HOURS_FORWARD * HOUR_MS,
}

export const historyStartMs = (mode: WeatherMode, nowMs: number) => {
    const cap = nowMs - COVERAGE_CAP_MS
    const published = publishedHistoryStartMs(MODE_TILE_TYPE[mode])
    return published == null ? cap : Math.min(nowMs, Math.max(published, cap))
}

export const forwardHorizonMs = (mode: WeatherMode, nowMs: number) => {
    const tileType = MODE_TILE_TYPE[mode]
    const published = publishedForecastHorizonMs(tileType)
    if (published != null) return Math.max(published, nowMs)
    return (forecastRunMs(tileType) ?? nowMs) + MODE_FORWARD_REACH_MS[mode]
}

const LOOKBEHIND_MS = 8 * HOUR_MS
const LOOKAHEAD_MS = 8 * HOUR_MS
const PLAY_PREFETCH_MS = 6 * HOUR_MS
const RADAR_WINDOW_MS = 3 * HOUR_MS

export const computeFetchWindow = (
    mode: WeatherMode,
    t: number,
    isPlaying: boolean,
    extentStart: number,
    extentEnd: number
) => {
    const lookbehind = mode === "radar" ? (isPlaying ? 0 : RADAR_WINDOW_MS) : LOOKBEHIND_MS
    const lookahead = mode === "radar" ? RADAR_WINDOW_MS : LOOKAHEAD_MS
    const playAhead = mode === "radar" ? 0 : PLAY_PREFETCH_MS
    const winStart = Math.max(extentStart, t - lookbehind)
    const winEnd = Math.min(extentEnd, t + lookahead + (isPlaying ? playAhead : 0))
    return { winStart, winEnd }
}

export const FRAME_BURST = 6

export const bracketHours = (winHours: number[], t: number) => {
    let before: number | null = null
    let after: number | null = null
    for (const h of winHours) {
        if (h <= t && (before === null || h > before)) before = h
        if (h >= t && (after === null || h < after)) after = h
    }
    const out: number[] = []
    if (before !== null) out.push(before)
    if (after !== null && after !== before) out.push(after)
    return out
}

export const frameCost = (hourMs: number, t: number, isPlaying: boolean) => {
    const dist = Math.abs(hourMs - t)
    return isPlaying && hourMs < t ? dist + LOOKAHEAD_MS : dist
}
